import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts/List/Root";
import { type AtticVariantStockFragment } from "@dashboard/graphql";
import { sectionNames } from "@dashboard/intl";
import { MONO_FONT_FAMILY } from "@dashboard/styles/monoFontFamily";
import { Box, Button, Input, Skeleton, Text } from "@saleor/macaw-ui-next";
import { type FormEvent, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { sectionMessages, stockMessages } from "../../messages";
import { atticLedgerUrl } from "../../urls";
import { formatCost, normalizeSku } from "../../utils";
import { AtticInventoryNav } from "../AtticInventoryNav/AtticInventoryNav";
import { AtticUnitsTable } from "../AtticUnitsTable/AtticUnitsTable";

export interface AtticStockLookupPageProps {
  /** The SKU in the URL; "" for none. */
  sku: string;
  /** undefined while loading; null when the SKU names nothing. */
  stock: AtticVariantStockFragment | null | undefined;
  loadFailed: boolean;
  /** The currency the average cost is shown in. */
  currency: string;
  onLookup: (sku: string) => void;
  onSetReserve: () => void;
  onAdjust: () => void;
}

const Figure = ({
  label,
  value,
  testId,
  warn = false,
}: {
  label: string;
  value: string | number;
  testId: string;
  warn?: boolean;
}) => (
  <Box display="flex" flexDirection="column" gap={1} __minWidth="110px">
    <Text size={2} color="default2">
      {label}
    </Text>
    <Text
      size={6}
      fontWeight="medium"
      color={warn ? "critical1" : "default1"}
      data-test-id={testId}
    >
      {value}
    </Text>
  </Box>
);

export const AtticStockLookupPage = ({
  sku,
  stock,
  loadFailed,
  currency,
  onLookup,
  onSetReserve,
  onAdjust,
}: AtticStockLookupPageProps) => {
  const intl = useIntl();
  const [typed, setTyped] = useState(sku);

  useEffect(() => setTyped(sku), [sku]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onLookup(normalizeSku(typed));
  };

  return (
    <ListPageLayout>
      <TopNav
        title={intl.formatMessage(sectionNames.atticInventory)}
        subtitle={
          <Text size={2} color="default2">
            {intl.formatMessage(stockMessages.subtitle)}
          </Text>
        }
      />
      <AtticInventoryNav current="stock" />
      <DashboardCard data-test-id="attic-stock-lookup">
        <DashboardCard.Content>
          <form onSubmit={submit}>
            <Box display="flex" gap={3} alignItems="flex-end">
              <Box flexGrow="1" __maxWidth="420px">
                <Input
                  label={intl.formatMessage(stockMessages.skuPlaceholder)}
                  value={typed}
                  autoFocus
                  onChange={event => setTyped(event.target.value)}
                  data-test-id="attic-stock-sku"
                />
              </Box>
              <Button type="submit" variant="primary" data-test-id="attic-stock-lookup-button">
                {intl.formatMessage(stockMessages.lookUp)}
              </Button>
            </Box>
          </form>
          {!sku && (
            <Text as="p" size={3} color="default2" marginTop={4} data-test-id="attic-stock-prompt">
              {intl.formatMessage(stockMessages.prompt)}
            </Text>
          )}
          {!!sku && loadFailed && (
            <Text as="p" size={3} color="critical1" marginTop={4} data-test-id="attic-stock-failed">
              {intl.formatMessage(sectionMessages.loadFailed)}
            </Text>
          )}
          {!!sku && !loadFailed && stock === null && (
            <Text
              as="p"
              size={3}
              color="default2"
              marginTop={4}
              data-test-id="attic-stock-not-found"
            >
              {intl.formatMessage(stockMessages.notFound, { sku })}
            </Text>
          )}
          {!!sku && !loadFailed && stock === undefined && (
            <Box marginTop={4}>
              <Skeleton />
            </Box>
          )}
        </DashboardCard.Content>
      </DashboardCard>
      {stock && (
        <>
          <DashboardCard data-test-id="attic-stock-position">
            <DashboardCard.Header>
              <DashboardCard.Title>
                <Box display="flex" gap={3} alignItems="baseline" flexWrap="wrap">
                  <Text size={5} style={{ fontFamily: MONO_FONT_FAMILY }}>
                    {stock.sku}
                  </Text>
                  <Text size={3} color="default2" data-test-id="attic-stock-name">
                    {stock.variant.product.name}
                    {stock.variant.name ? ` / ${stock.variant.name}` : ""}
                  </Text>
                </Box>
              </DashboardCard.Title>
              <DashboardCard.Toolbar>
                <Box display="flex" gap={2}>
                  <Link to={atticLedgerUrl({ sku: stock.sku })} data-test-id="attic-stock-history">
                    <Button as="span" variant="secondary" size="small">
                      {intl.formatMessage(stockMessages.history)}
                    </Button>
                  </Link>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={onAdjust}
                    data-test-id="attic-stock-adjust"
                  >
                    {intl.formatMessage(stockMessages.adjust)}
                  </Button>
                  <Button
                    variant="primary"
                    size="small"
                    onClick={onSetReserve}
                    data-test-id="attic-stock-set-reserve"
                  >
                    {intl.formatMessage(stockMessages.setReserve)}
                  </Button>
                </Box>
              </DashboardCard.Toolbar>
            </DashboardCard.Header>
            <DashboardCard.Content>
              <Box display="flex" gap={8} flexWrap="wrap">
                <Figure
                  label={intl.formatMessage(stockMessages.onHand)}
                  value={stock.onHand}
                  testId="attic-stock-on-hand"
                />
                <Figure
                  label={intl.formatMessage(stockMessages.reserved)}
                  value={stock.reserved}
                  testId="attic-stock-reserved"
                />
                <Figure
                  label={intl.formatMessage(stockMessages.sellable)}
                  value={stock.sellable}
                  testId="attic-stock-sellable"
                />
                <Figure
                  label={intl.formatMessage(stockMessages.saleorQuantity)}
                  value={stock.saleorQuantity ?? intl.formatMessage(stockMessages.none)}
                  testId="attic-stock-saleor"
                  warn={!stock.inSync}
                />
                <Figure
                  label={intl.formatMessage(stockMessages.averageCost)}
                  value={
                    stock.averageCost === null || stock.averageCost === undefined
                      ? intl.formatMessage(stockMessages.none)
                      : formatCost(stock.averageCost, currency, intl)
                  }
                  testId="attic-stock-average-cost"
                />
              </Box>
              {stock.reserveReason && (
                <Text as="p" size={2} color="default2" marginTop={3}>
                  {stock.reserveReason}
                </Text>
              )}
              {!stock.inSync && (
                <Text
                  as="p"
                  size={3}
                  color="critical1"
                  marginTop={4}
                  data-test-id="attic-stock-out-of-sync"
                >
                  {intl.formatMessage(stockMessages.outOfSync)}
                </Text>
              )}
            </DashboardCard.Content>
          </DashboardCard>
          <DashboardCard data-test-id="attic-stock-units">
            <DashboardCard.Header>
              <DashboardCard.Title>
                {intl.formatMessage(stockMessages.unitsTitle)}
              </DashboardCard.Title>
            </DashboardCard.Header>
            <DashboardCard.Content paddingX={0}>
              <AtticUnitsTable
                units={stock.units}
                show={{ location: true, lot: true }}
                emptyText={intl.formatMessage(stockMessages.noUnits)}
              />
            </DashboardCard.Content>
          </DashboardCard>
        </>
      )}
    </ListPageLayout>
  );
};

export default AtticStockLookupPage;
